import { AfterViewInit, Component, computed, CUSTOM_ELEMENTS_SCHEMA, ElementRef, forwardRef, input, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface RidAltchaCaptchaStrings {
  error: string;
  expired: string;
  label: string;
  verified: string;
  verifying: string;
  waitAlert: string;
}

const DEFAULT_STRINGS: RidAltchaCaptchaStrings = {
  error: 'Verification failed. Try again later.',
  expired: 'Verification expired. Try again.',
  label: "I'm not a robot",
  verified: 'Verified',
  verifying: 'Verifying...',
  waitAlert: 'Verifying... please wait.',
};

/**
 * Wraps the third-party `<altcha-widget>` custom element (registered globally by importing the `altcha` package, e.g. in `main.ts`) as a reactive-forms `ControlValueAccessor`. The consuming app owns the backend base URL (`challengeUrl`) and, optionally, localized copy (`strings`).
 */
@Component({
  selector: 'rid-altcha-captcha',
  template: `<!--suppress HtmlUnknownTag -->
    <altcha-widget
      #altchaWidget
      [attr.strings]="localizationStrings()"
      floating
      hidefooter
      hidelogo
      [attr.challengeurl]="challengeUrl()"
      customfetch="ridAltchaCustomFetch"
    ></altcha-widget>`,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RidAltchaCaptcha),
      multi: true,
    },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RidAltchaCaptcha implements ControlValueAccessor, AfterViewInit {
  challengeUrl = input.required<string>();
  strings = input<Partial<RidAltchaCaptchaStrings>>();

  protected localizationStrings = computed(() => JSON.stringify({ ...DEFAULT_STRINGS, ...this.strings() }));

  @ViewChild('altchaWidget', { static: true }) altchaWidget!: ElementRef;

  value = '';
  onChange: CallableFunction = () => undefined;
  onTouched: CallableFunction = () => undefined;

  ngAfterViewInit(): void {
    const el = this.altchaWidget.nativeElement as HTMLElement;
    el.addEventListener('statechange', (ev) => {
      const { detail } = ev as CustomEvent;
      if (detail) {
        const { payload, state } = detail;
        this.onStateChange(state, payload);
      }
    });
  }

  writeValue(value: string): void {
    this.value = value;
  }

  registerOnChange(fn: CallableFunction): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: CallableFunction): void {
    this.onTouched = fn;
  }

  onStateChange(state: 'unverified' | 'verifying' | 'verified' | 'error', payload = '') {
    this.value = state === 'verified' ? payload : '';
    this.onChange(this.value);
    this.onTouched();
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).ridAltchaCustomFetch = function (url: string) {
  return fetch(url, {
    credentials: 'include',
  });
};
